import { useMutation } from '@apollo/client';
import type {
  InsertInternalAuditInput,
  InsertInternalAuditMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertInternalAuditDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useInsertInternalAuditTRPC } from './useInsertInternalAuditTRPC';

export const useInsertInternalAudit = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [insertInternalAuditGraphQL, graphqlState] = useMutation(
    InsertInternalAuditDocument,
    {
      update: (cache) => {
        evictField(cache, 'internal_audit_entity');
        evictField(cache, 'internal_audit_entity_aggregate');
        evictField(cache, 'business_area');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useInsertInternalAuditTRPC();

  const insertInternalAudit = async (
    variables: InsertInternalAuditInput
  ): Promise<InsertInternalAuditMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertInternalAudit({
        Title: variables.Title ?? '',
        Description: variables.Description,
        BusinessArea: variables.BusinessArea,
        CustomAttributeData: variables.CustomAttributeData,
        OwnerUserIds: variables.OwnerUserIds ?? [],
        OwnerGroupIds: variables.OwnerGroupIds ?? [],
        ContributorUserIds: variables.ContributorUserIds ?? [],
        ContributorGroupIds: variables.ContributorGroupIds ?? [],
        ViewerUserIds: variables.ViewerUserIds,
        ViewerGroupIds: variables.ViewerGroupIds,
        TagTypeIds: variables.TagTypeIds ?? [],
        DepartmentTypeIds: variables.DepartmentTypeIds ?? [],
      });
    }

    const result = await insertInternalAuditGraphQL({
      variables: { Input: variables },
    });
    if (!result.data) {
      throw new Error('Failed to insert internal audit');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertInternalAudit,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertInternalAudit,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
